/**
 * Soft-launch access PIN — client-side only (same model as HOTTR.men / HOTTR.tv).
 * PIN comes from config/env (build-time inject of PIN_ACCESS_CODE).
 * Not real security — just a soft gate for preview access.
 */

import { env, hasPinGate } from '@/config/env';

/** Unlock lasts this long, then the PIN must be re-entered. */
export const PIN_TTL_MS = 24 * 60 * 60 * 1000;

const KEY = 'hottr.app.pin_ok_v1';

function readConfiguredPin(): string {
  return env.pinAccessCode.replace(/\s/g, '');
}

export function isPinGateEnabled(): boolean {
  return hasPinGate();
}

export function isPinUnlocked(): boolean {
  if (!isPinGateEnabled()) return true;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const unlockedAt = Number(raw);
    if (!Number.isFinite(unlockedAt) || unlockedAt <= 0) {
      localStorage.removeItem(KEY);
      return false;
    }
    const age = Date.now() - unlockedAt;
    if (age < 0 || age >= PIN_TTL_MS) {
      localStorage.removeItem(KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function pinUnlockRemainingMs(): number {
  if (!isPinGateEnabled()) return 0;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return 0;
    const unlockedAt = Number(raw);
    if (!Number.isFinite(unlockedAt)) return 0;
    return Math.max(0, PIN_TTL_MS - (Date.now() - unlockedAt));
  } catch {
    return 0;
  }
}

export function unlockPin(): void {
  try {
    localStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function lockPin(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function checkPin(value: string): boolean {
  const expected = readConfiguredPin();
  if (!expected) return true;
  return value.replace(/\s/g, '') === expected;
}

/**
 * Keypad box count / auto-submit length. Numeric codes of 4–12 digits use the
 * real length; anything else (empty, alphanumeric, out of range) draws 6 boxes.
 * Length policy is locked — do not change the bounds.
 */
export function keypadPinSpec(code: string): { length: number; numeric: boolean } {
  const expected = code.replace(/\s/g, '');
  const numeric =
    /^\d+$/.test(expected) && expected.length >= 4 && expected.length <= 12;
  return { numeric, length: numeric ? expected.length : 6 };
}

/**
 * Pure digit buffer. Side-effect free so React (including Strict Mode) can
 * replay it. `ready` is true only when this append reaches `pinLength` — that
 * is the single moment the keypad should auto-submit.
 */
export function appendPinDigit(
  digits: readonly string[],
  digit: string,
  pinLength: number,
): { digits: string[]; ready: boolean } {
  if (digits.length >= pinLength) {
    return { digits: [...digits], ready: false };
  }
  const next = [...digits, digit];
  return { digits: next, ready: next.length === pinLength };
}
